import { Component, ElementRef, HostListener, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { ApplicationFacade, NotificationFacade } from '@ridango/components';
import {
    BankcardTicket,
    BankcardTicketsFacade,
    BankcardTicketsService,
    BankcardTransaction
} from '@ridango/components/lib/bankcard-tickets';
import { BaseComponent, CoreFacade, Language } from '@ridango/core';
import Env from '@ridango/core/env';
import { AppConfigService, EcommFormService } from '@ridango/core/lib/services';

import { saveAs } from 'file-saver';
import { takeUntil } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { TransactionMessage } from '@ridango/core/lib/models/ecomm-form.interface';

@Component({
    selector: 'rwidget-bankcard-tickets',
    templateUrl: './bankcard-tickets.component.html',
    styleUrls: ['./bankcard-tickets.component.scss']
})
export class BankcardTicketsComponent extends BaseComponent implements OnInit, OnDestroy {
    @Input() differentRegionTickets = false;
    @Input() regionId = null;
    @ViewChild('iframe', { static: false }) iframe: ElementRef;

    private transaction: BankcardTransaction;
    private isDarkMode = false;
    displayIframe = false;
    tpsEndpoint: string;
    siteLanguage: Language;
    ticketsError$ = this.bankcardTicketsFacade.ticketsError$;
    ticketsLoading$ = this.bankcardTicketsFacade.ticketsLoading$;
    tickets$ = this.bankcardTicketsFacade.tickets$;
    hslTicketsLoading$ = this.bankcardTicketsFacade.hslTicketsLoading$;
    hslTickets$ = this.bankcardTicketsFacade.hslTickets$;
    selectedLanguage$: Observable<Language> = this.coreFacade.selectedLanguage$;
    private triesLeft = 3;

    constructor(
        @Inject('ENV') public env: Env,
        private bankcardTicketsFacade: BankcardTicketsFacade,
        private translate: TranslateService,
        private notification: NotificationFacade,
        private bankcardTicketsService: BankcardTicketsService,
        private coreFacade: CoreFacade,
        private appConfigService: AppConfigService,
        private applicationFacade: ApplicationFacade,
        private ecommFormService: EcommFormService
    ) {
        super();
        this.tpsEndpoint = this.env.TPS_BANKCARD_IFRAME_URL;
    }

    ngOnInit(): void {
        this.ecommFormService
            .watchThemeChanges(
                this.applicationFacade.highContrast$,
                this.componentDestroyed$,
                () => this.iframe,
                () => this.displayIframe
            )
            .subscribe((isDarkMode) => {
                this.isDarkMode = isDarkMode;
            });

        this.translate?.onLangChange?.subscribe(({ lang }) => {
            if (this.transaction && this.transaction.lang !== lang) {
                this.newSearch();
            }
        });

        const appProperties = this.regionId && this.appConfigService.getAppProperty(this.regionId);
        this.bankcardTicketsFacade.fetchTransaction(this.getTransactionRegion());
        this.bankcardTicketsFacade.transaction$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((transaction: BankcardTransaction) => {
                if (!transaction) {
                    return;
                }
                if (this.iframe && this.iframe.nativeElement.src !== this.tpsEndpoint) {
                    this.iframe.nativeElement.src = this.tpsEndpoint;
                }
                this.transaction = {
                    ...transaction,
                    hostApp: appProperties?.hostApp ?? this.env.CEMV_HOST_APP,
                    lang: this.translate.currentLang,
                    useNewCardTokenForm: true
                };
                this.displayIframe = true;
            });

        this.bankcardTicketsFacade.error$.pipe(takeUntil(this.componentDestroyed$)).subscribe((error) => {
            if (error) {
                this.handleError(error);
            }
        });

        this.selectedLanguage$.pipe(takeUntil(this.componentDestroyed$)).subscribe((lang) => {
            this.siteLanguage = lang;
            if (this.transaction && this.transaction.lang !== lang.code) {
                this.newSearch();
            }
        });

        this.bankcardTicketsFacade.ticketsError$.pipe(takeUntil(this.componentDestroyed$)).subscribe((error) => {
            if (error) {
                if (
                    (error.error === 'TRANSACTION_NOT_FILLED_BY_TPS' || error.error === 'TRANSACTION_NOT_FOUND') &&
                    this.triesLeft > 0
                ) {
                    this.triesLeft--;
                    if (this.transaction && this.transaction.transactionId) {
                        this.bankcardTicketsFacade.fetchTickets(
                            this.transaction.transactionId,
                            this.translate.currentLang
                        );
                        if (this.differentRegionTickets) {
                            this.bankcardTicketsFacade.fetchHslTickets(this.transaction.transactionId);
                        }
                    }
                } else {
                    this.transaction = null;
                    this.handleError(error);
                    this.bankcardTicketsFacade.fetchTransaction(this.getTransactionRegion());
                }
            }
        });
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this.bankcardTicketsFacade.cleanState();
        this.notification.removeAll();
    }

    getTransactionRegion() {
        if (this.differentRegionTickets) {
            return 1;
        } else {
            return this.regionId ? this.regionId : this.env.REGION_ID;
        }
    }

    @HostListener('window:message', ['$event'])
    iframeMessage(event) {
        if (event.data) {
            if ('ready' in event.data && event.data.ready) {
                this.sendTransactionInfoToTPS();
            } else if (event.data.responseCodeName && event.data.responseCodeName === 'APPROVED') {
                this.notification.removeAll();
                this.bankcardTicketsFacade.fetchTickets(this.transaction.transactionId, this.translate.currentLang);
                if (this.differentRegionTickets) {
                    this.bankcardTicketsFacade.fetchHslTickets(this.transaction.transactionId);
                }
                this.displayIframe = false;
            }
        }
    }

    newSearch() {
        if (this.iframe) {
            this.iframe.nativeElement.src = '';
        }
        this.transaction = null;
        this.bankcardTicketsFacade.cleanState();
        this.notification.removeAll();
        this.triesLeft = 3;
        this.bankcardTicketsFacade.fetchTransaction(this.getTransactionRegion());
    }

    sendTransactionInfoToTPS() {
        this.iframe.nativeElement.contentWindow.postMessage(this.getTransactionMessage(), this.tpsEndpoint);
    }

    private getTransactionMessage(): TransactionMessage {
        return this.ecommFormService.getTransactionMessage(this.transaction, this.isDarkMode, {
            useShortCardTokenForm: false,
            use3DSCardTokenForm: true,
            permissiveSubmitButton: false,
            placeholders: true
        });
    }

    handleError(e: any) {
        if (e.error.description) {
            this.notification.error(this.translate.instant(e.error.description));
        } else {
            if (!e.error.sources) {
                this.notification.error(this.translate.instant(e.error));
            }
        }
    }

    getPdf(ticket: BankcardTicket) {
        this.bankcardTicketsService
            .fetchPDF(ticket.jwt_token, this.translate.currentLang)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(
                (d) => {
                    saveAs(d, `Receipt.pdf`);
                },
                () => {
                    console.log(`PDF error`);
                }
            );
    }
}
